import { Inbox } from 'lucide-react';
import React, { useContext } from 'react';
import { InCardContext } from '~/components/ui/card/Card';
import { EmptyState } from '~/components/ui/empty-state';
import { LoadError } from '~/components/ui/load-error';
import { ClickableCell, ClickableRow, RowAnchor } from '~/components/ui/table/row-link';
import { TableRowsSkeleton } from '~/components/ui/table/TableRowsSkeleton';
import {
  TABLE_BODY,
  TABLE_DIVIDER,
  TABLE_BORDER,
  TABLE_HEAD_ROW,
  TABLE_ROW,
  TABLE_ROW_HOVER,
  TABLE_TD,
  TABLE_TH,
  hasDivider,
} from '~/components/ui/table/tokens';

const ACTIONS_HEADER = 'Actions';
const SKELETON_ROWS = 6;

export interface PanelTableColumn<T> {
  header: string;
  /** Tailwind width class for the column, e.g. "w-64". */
  width?: string;
  render: (row: T) => React.ReactNode;
}

interface PanelTableProps<T> {
  columns: PanelTableColumn<T>[];
  data: T[];
  getRowId?: (row: T, index: number) => string | number;
  /** Required: what is empty, and what to do about it. */
  emptyMessage: string;
  emptyDescription: string;
  /** First load: the real header over skeleton rows. */
  loading?: boolean;
  /** A persistent load failure, rendered as the shared `LoadError` in place of the rows. */
  error?: { what: string; message?: string; onRetry?: () => void };
  /** Defaults to framed outside a `Card` and unframed inside one; a card's table never has an outer border. */
  framed?: boolean;
  /** Enables the sticky-header scroll box, e.g. "420px". Omit for an unbounded table. */
  maxHeight?: string;
  /** Required — no table in the codebase currently has one. */
  ariaLabel: string;
  testId?: string;
  /**
   * Where a click anywhere on the row opens, or null for a row with no destination (inert: no
   * cursor, no hover). Same behaviour as `DataTable rowHref`: role="link", Enter/Space, the shared
   * click guard, a real `<Link>` in the first cell, and an `Actions` column excluded.
   */
  rowHref?: (row: T) => string | null;
  /** Accessible name for a linked row. Without it the row announces its cell text. */
  rowLabel?: (row: T) => string;
  /** Select-only pickers: makes the whole row clickable; `selected` rows are highlighted. Ignored for a row `rowHref` links. */
  onRowClick?: (row: T) => void;
  selected?: (row: T) => boolean;
  /**
   * Fixed table layout. Columns then honour their `width` classes instead of sizing to content,
   * so two panels with the same columns line up (e.g. two expanded Instances' container tables).
   * Unspecified columns share the remaining width equally. Long cells wrap rather than widen.
   */
  fixed?: boolean;
}

const LINKED_ROW_CLASS = `cursor-pointer ${TABLE_ROW_HOVER}`;

/**
 * Read-only table for detail panes (env vars, volumes, ports, network, logs,
 * server tasks). No sorting, no pagination, no filtering — use `DataTable` for
 * a collection the user browses, sorts, or acts on per row. Framed (rounded border) by default,
 * so it sits inside a section's padding; `framed={false}` runs it edge to edge in a `CardBody`.
 */
export function PanelTable<T>({
  columns,
  data,
  getRowId,
  emptyMessage,
  emptyDescription,
  loading = false,
  error,
  framed: framedProp,
  maxHeight,
  ariaLabel,
  testId,
  rowHref,
  rowLabel,
  onRowClick,
  selected,
  fixed,
}: PanelTableProps<T>) {
  const inCard = useContext(InCardContext);
  const framed = framedProp ?? !inCard;
  const frameClass = framed ? TABLE_BORDER : '';
  const ids = columns.map((column) => (column.header === ACTIONS_HEADER ? 'actions' : undefined));

  if (error) {
    return <LoadError {...error} testId={testId ? `${testId}-error` : undefined} />;
  }

  if (loading) {
    return (
      <TableRowsSkeleton
        rows={SKELETON_ROWS}
        framed={framed}
        testId={testId ? `${testId}-loading` : undefined}
        columns={columns.map((column, index) => ({
          id: ids[index],
          header: column.header,
          className: column.width,
        }))}
      />
    );
  }

  if (data.length === 0) {
    return (
      <div className={frameClass} data-testid={testId ? `${testId}-empty` : undefined}>
        <EmptyState
          icon={<Inbox className="size-8" aria-hidden="true" />}
          title={emptyMessage}
          description={emptyDescription}
        />
      </div>
    );
  }

  const table = (
    <table
      className={fixed ? 'w-full table-fixed' : 'min-w-full'}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {/* Opaque: this header is sticky, so a translucent background would show the rows scrolling underneath it. */}
      <thead className="sticky top-0 z-10 bg-white dark:bg-gray-900">
        <tr className={TABLE_HEAD_ROW}>
          {columns.map((column, index) => (
            <th
              key={index}
              scope="col"
              className={`${TABLE_TH} text-left ${hasDivider(ids, index) ? TABLE_DIVIDER : ''} ${column.width ?? ''}`}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className={TABLE_BODY}>
        {data.map((row, index) => {
          const key = getRowId ? getRowId(row, index) : index;
          const href = rowHref?.(row) ?? null;
          const rowTestId = testId ? `${testId}-row` : undefined;
          const baseClass = `${TABLE_ROW} align-top ${selected?.(row) ? 'bg-brand-50 dark:bg-brand-500/10' : ''}`;
          const firstColumn = columns.findIndex((column) => column.header !== ACTIONS_HEADER);
          const cells = columns.map((column, columnIndex) => {
            const clickable = href !== null && column.header !== ACTIONS_HEADER;
            const cellClass = `${TABLE_TD} break-all ${hasDivider(ids, columnIndex) ? TABLE_DIVIDER : ''} ${clickable ? 'cursor-pointer' : ''}`;
            const content =
              href !== null && columnIndex === firstColumn ? (
                <RowAnchor key={columnIndex} href={href}>
                  {column.render(row)}
                </RowAnchor>
              ) : (
                column.render(row)
              );
            return clickable ? (
              <ClickableCell key={columnIndex} href={href} className={cellClass}>
                {content}
              </ClickableCell>
            ) : (
              <td key={columnIndex} className={cellClass}>
                {content}
              </td>
            );
          });
          if (href !== null) {
            return (
              <ClickableRow
                key={key}
                href={href}
                rowLabel={rowLabel?.(row)}
                className={`${baseClass} ${LINKED_ROW_CLASS}`}
                testId={rowTestId}
              >
                {cells}
              </ClickableRow>
            );
          }
          return (
            <tr
              key={key}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`${baseClass} ${onRowClick ? LINKED_ROW_CLASS : ''}`}
              data-testid={rowTestId}
            >
              {cells}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div
      className={`overflow-x-auto ${frameClass} ${maxHeight ? 'overflow-y-auto max-h-(--panel-max-h)' : ''}`}
      style={maxHeight ? ({ '--panel-max-h': maxHeight } as React.CSSProperties) : undefined}
    >
      {table}
    </div>
  );
}
